/**
 * The Company Registry step (KYB): find → select → confirm, or enter by hand.
 *
 * Which fields show, and which are required, come from `snapshot.step`
 * (server-resolved from the pinned workflow). Prefill comes from the registry
 * search; the server re-fetches the record on submit, so a prefill the
 * applicant does not touch is never what decides the result.
 *
 * Owns its own POSTs and hands back the next snapshot, like the other widgets.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { CollectionFlowError, postCollectionFlowEvent } from './client';
import { DateInput } from './DateInput';
import { pastDateRange } from './dateField';
import {
  COMPANY_EVENT,
  type CompanySearchResult,
  type CompanyStepView,
  digitsOf,
  searchCompany,
  submissionOf,
  visibleFields,
} from './company';
import type { CollectionFlowSnapshot } from './types';

interface CompanyStepProps {
  token: string;
  view: CompanyStepView;
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
}

type Phase =
  | { kind: 'find' }
  | { kind: 'select'; query: string; results: CompanySearchResult[] }
  | { kind: 'confirm'; pick: CompanySearchResult | null };

export function CompanyStep({ token, view, onAdvance }: CompanyStepProps) {
  const { t } = useTranslation();
  // Only a country whose register the server can read is searched; anywhere
  // else the applicant types the company in, so the find screen (a Thai
  // 13-digit search) is never shown.
  const searchable = view.search.registration_number;
  const [phase, setPhase] = useState<Phase>(
    searchable ? { kind: 'find' } : { kind: 'confirm', pick: null },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errorText = (e: unknown) =>
    e instanceof CollectionFlowError && e.code
      ? t(`company.error.${e.code}`, {
          defaultValue: t('company.error.generic'),
        })
      : t('company.error.generic');

  if (phase.kind === 'find') {
    return (
      <FindCompany
        busy={busy}
        country={view.country}
        error={error}
        onManual={() => {
          setError(null);
          setPhase({ kind: 'confirm', pick: null });
        }}
        onSearch={async (query) => {
          setBusy(true);
          setError(null);
          try {
            const results = await searchCompany(token, query);
            setPhase({ kind: 'select', query, results });
          } catch (e) {
            setError(errorText(e));
          } finally {
            setBusy(false);
          }
        }}
      />
    );
  }

  if (phase.kind === 'select') {
    return (
      <SelectCompany
        onAgain={() => setPhase({ kind: 'find' })}
        onManual={() => setPhase({ kind: 'confirm', pick: null })}
        onSelect={(pick) => setPhase({ kind: 'confirm', pick })}
        query={phase.query}
        results={phase.results}
      />
    );
  }

  return (
    <ConfirmCompany
      key={phase.pick?.registration_number ?? 'manual'}
      onBack={searchable ? () => setPhase({ kind: 'find' }) : undefined}
      onSubmit={async (companyName, values) => {
        const submission = submissionOf(phase.pick ? 'registry' : 'manual', companyName, values);
        onAdvance(await postCollectionFlowEvent(token, COMPANY_EVENT, submission));
      }}
      pick={phase.pick}
      view={view}
    />
  );
}

function FindCompany({
  busy,
  country,
  error,
  onManual,
  onSearch,
}: {
  busy: boolean;
  country: string;
  error: string | null;
  onManual: () => void;
  onSearch: (query: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [number, setNumber] = useState('');
  const ready = digitsOf(number).length === 13;
  return (
    <form
      className="details-form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (ready && !busy) void onSearch(number);
      }}
    >
      <h1 className="step-title">{t('company.find.title')}</h1>
      <p className="step-text">{t('company.find.lead')}</p>
      <div className="details-field">
        <span className="details-label">{t('company.find.country')}</span>
        <p className="details-input" aria-readonly="true">
          {t(`company.country.${country}`, { defaultValue: country })}
        </p>
      </div>
      <div className="details-field">
        <label className="details-label" htmlFor="company-number">
          {t('company.find.number')}
        </label>
        <input
          aria-describedby="company-number-hint"
          autoComplete="off"
          className="details-input"
          id="company-number"
          inputMode="numeric"
          onChange={(e) => setNumber(e.target.value)}
          placeholder="0105561147772"
          value={number}
        />
        <span className="step-text" id="company-number-hint">
          {t('company.find.numberHint')}
        </span>
      </div>
      {error ? (
        <p className="alert" role="alert">
          {error}
        </p>
      ) : null}
      <div className="actions">
        <button className="btn" disabled={!ready || busy} type="submit">
          {t('company.find.submit')}
        </button>
        <button className="btn btn--ghost" onClick={onManual} type="button">
          {t('company.find.manual')}
        </button>
      </div>
    </form>
  );
}

function SelectCompany({
  onAgain,
  onManual,
  onSelect,
  query,
  results,
}: {
  onAgain: () => void;
  onManual: () => void;
  onSelect: (pick: CompanySearchResult) => void;
  query: string;
  results: CompanySearchResult[];
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<string | null>(results[0]?.registration_number ?? null);
  const pick = results.find((r) => r.registration_number === chosen) ?? null;
  return (
    <div className="details-form">
      <h1 className="step-title">{t('company.select.title')}</h1>
      <p className="step-text">{t('company.select.lead', { query })}</p>
      {results.length === 0 ? (
        <p className="step-text">{t('company.select.none')}</p>
      ) : (
        <div role="radiogroup" aria-label={t('company.select.title')} className="company-results">
          {results.map((r) => (
            <label aria-label={r.name} className="company-result" key={r.registration_number}>
              <input
                checked={chosen === r.registration_number}
                name="company"
                onChange={() => setChosen(r.registration_number)}
                type="radio"
                value={r.registration_number}
              />
              <span className="company-result__body">
                <span className="company-result__name">{r.name}</span>
                <span className="step-text">
                  {t('company.select.reg', { number: r.registration_number })}
                </span>
                <span className="company-result__pills">
                  <span className="pill">
                    {t(`company.select.status.${r.status}`, {
                      defaultValue: r.status,
                    })}
                  </span>
                  {r.company_type ? <span className="pill">{r.company_type}</span> : null}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
      <p className="step-text">
        {t('company.select.notFound')}{' '}
        <button className="link-button" onClick={onManual} type="button">
          {t('company.select.manual')}
        </button>
      </p>
      <div className="actions">
        <button
          className="btn"
          disabled={!pick}
          onClick={() => pick && onSelect(pick)}
          type="button"
        >
          {t('company.select.submit')}
        </button>
        <button className="btn btn--ghost" onClick={onAgain} type="button">
          {t('company.select.again')}
        </button>
      </div>
    </div>
  );
}

function ConfirmCompany({
  onBack,
  onSubmit,
  pick,
  view,
}: {
  /** Absent where there is no search to go back to. */
  onBack?: () => void;
  onSubmit: (companyName: string, values: Record<string, string>) => Promise<void>;
  pick: CompanySearchResult | null;
  view: CompanyStepView;
}) {
  const { t } = useTranslation();
  const fields = visibleFields(view);
  // An incorporation date is in the past; the picker opens on today.
  const pastRange = useMemo(() => pastDateRange(), []);
  const [name, setName] = useState(pick?.name ?? '');
  const [values, setValues] = useState<Record<string, string>>(() => ({
    ...(pick?.prefill ?? {}),
    ...(pick ? { registration_number: pick.registration_number } : {}),
  }));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFailure(null);
    try {
      await onSubmit(name, values);
    } catch (err) {
      const byField = err instanceof CollectionFlowError ? err.details?.fields : undefined;
      if (byField && typeof byField === 'object') {
        setErrors(byField as Record<string, string>);
      } else {
        setFailure(
          err instanceof CollectionFlowError && err.code
            ? t(`company.error.${err.code}`, {
                defaultValue: t('company.error.generic'),
              })
            : t('company.error.generic'),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  // The server's per-field message for `required` is the one word; anything
  // else is its own sentence (format rules), shown as sent.
  const fieldError = (key: string) =>
    errors[key] === 'required' ? t('company.error.required') : errors[key];

  return (
    <form className="details-form" onSubmit={(e) => void submit(e)}>
      <h1 className="step-title">
        {pick ? t('company.confirm.title') : t('company.confirm.titleManual')}
      </h1>
      {pick ? <p className="step-text">{t('company.confirm.lead')}</p> : null}
      <label className="details-field">
        <span className="details-label">{t('company.confirm.name')}</span>
        <input
          aria-invalid={errors.company_name ? true : undefined}
          className="details-input"
          onChange={(e) => setName(e.target.value)}
          required
          value={name}
        />
        {errors.company_name ? <span className="alert">{fieldError('company_name')}</span> : null}
      </label>
      {fields.map(({ name: key, required }) =>
        key === 'incorporation_date' ? (
          // A div, not a <label>: the field is a button that opens a calendar.
          <div className="details-field" key={key}>
            <span className="details-label" aria-hidden="true">
              {t(`company.field.${key}`, { defaultValue: key })}
              {required ? null : (
                <span className="step-text"> · {t('company.confirm.optional')}</span>
              )}
            </span>
            <DateInput
              label={t(`company.field.${key}`, { defaultValue: key })}
              name={key}
              onChange={(iso) => setValues({ ...values, [key]: iso })}
              placeholder=""
              range={pastRange}
              value={values[key] ?? ''}
            />
            {errors[key] ? <span className="alert">{fieldError(key)}</span> : null}
          </div>
        ) : (
          <label className="details-field" key={key}>
            <span className="details-label">
              {t(`company.field.${key}`, { defaultValue: key })}
              {required ? null : (
                <span className="step-text"> · {t('company.confirm.optional')}</span>
              )}
            </span>
            <input
              aria-invalid={errors[key] ? true : undefined}
              className="details-input"
              onChange={(e) => setValues({ ...values, [key]: e.target.value })}
              readOnly={!!pick && key === 'registration_number'}
              type={key === 'email' ? 'email' : 'text'}
              value={values[key] ?? ''}
            />
            {errors[key] ? <span className="alert">{fieldError(key)}</span> : null}
          </label>
        ),
      )}
      {failure ? (
        <p className="alert" role="alert">
          {failure}
        </p>
      ) : null}
      <div className="actions">
        <button className="btn" disabled={busy || !name.trim()} type="submit">
          {t('company.confirm.submit')}
        </button>
        {onBack ? (
          <button className="btn btn--ghost" onClick={onBack} type="button">
            {t('company.confirm.back')}
          </button>
        ) : null}
      </div>
    </form>
  );
}
